import type { Language } from '@eformulieren/engine'
import type { Editor } from '@tiptap/core'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { FIND_KEY, type FindChange, type FindMatch, findMatches, findStateOf, firstFrom } from './find.js'

/**
 * De zoekbalk van één rich-text-veld (R-166, G047, R-139).
 *
 * Het patroon komt van de zoekbalk in een tekstverwerker (Google Docs, de zoekbalk van de browser):
 * een zoekvak, een telling "2 van 5", vorige en volgende, en Escape om te sluiten. Enter gaat naar de
 * volgende treffer en Shift+Enter naar de vorige. De balk hoort bij dit veld en zoekt alleen hierin;
 * de bronnen staan in ontwerp 6.3.
 *
 * **De focus blijft in het zoekvak.** De knoppen nemen hem niet over (`preventDefault` op de
 * mousedown, zoals de werkbalk), zodat wie klikt meteen verder kan typen. Sluiten zet de focus terug
 * in het veld, met de huidige treffer geselecteerd, zodat de invuller verder kan waar hij zocht.
 *
 * **De telling voor een schermlezer komt vertraagd**, in een eigen statusregio, en niet bij elke
 * toets: anders hoort hij bij "zeegras" zeven tellingen achter elkaar. Het zoekvak beschrijft zich
 * ook met de telling, zodat die bij terugkeren naar het vak meteen klinkt.
 */

export interface FindBarProps {
  readonly editor: Editor
  readonly taal: Language
  /** Het label van het veld, voor de naam van de balk en het zoekvak. */
  readonly veldNaam: string
  /** Gaat omhoog bij elke nieuwe Ctrl+F terwijl de balk al open is: dan krijgt het zoekvak de focus weer. */
  readonly signaal: number
  /**
   * De zoekterm van de vorige keer in dit veld, of leeg. Zoals de zoekbalk van een browser: wie
   * opnieuw opent zoekt meestal verder naar hetzelfde, en wie iets anders zoekt typt eroverheen.
   */
  readonly begin: string
  /** De balk gaat dicht, met de treffer die de huidige was (of niets) en de zoekterm. */
  readonly onClose: (match: FindMatch | undefined, query: string) => void
}

/** Hoe lang de telling wacht voordat een schermlezer haar hoort. */
const ANNOUNCE_AFTER_MS = 500

export function FindBar({ editor, taal: language, veldNaam: fieldName, signaal: signal, begin, onClose }: FindBarProps): ReactNode {
  const [query, setQuery] = useState(begin)
  const input = useRef<HTMLInputElement | null>(null)
  const countId = useId()
  const state = findStateOf(editor.state)
  const words = WORDS[language]

  const run = (change: FindChange): void => {
    editor.view.dispatch(editor.state.tr.setMeta(FIND_KEY, change))
  }

  const search = (text: string): void => {
    // Vanaf waar de invuller staat, zoals in een tekstverwerker.
    run({ query: text, active: firstFrom(findMatches(editor.state.doc, text), editor.state.selection.from) })
  }

  // Een zoekterm van de vorige keer zoekt meteen, vanaf de cursor van nu.
  useEffect(() => {
    if (begin.trim() !== '') search(begin)
    // Alleen bij openen; daarna zoekt het typen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bij openen en bij elke volgende Ctrl+F: de focus in het zoekvak, met wat er staat geselecteerd.
  useEffect(() => {
    input.current?.focus()
    input.current?.select()
  }, [signal])

  // De huidige treffer in beeld, zonder de focus uit het zoekvak te halen.
  useEffect(() => {
    if (state.active < 0) return
    const element = editor.view.dom.querySelector('.ef-zoektreffer--huidig')
    element?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [editor, state.active, state.matches])

  const hasQuery = query.trim() !== ''
  const total = state.matches.length
  const countText = !hasQuery ? '' : total === 0 ? words.geen : words.van(state.active + 1, total)

  const [announced, setAnnounced] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setAnnounced(countText), ANNOUNCE_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [countText])

  const step = (by: number): void => {
    if (total === 0) return
    run({ active: state.active + by })
  }

  const close = (): void => {
    const match = state.matches[state.active]
    run({ query: '' })
    onClose(match, query)
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      step(event.shiftKey ? -1 : 1)
    } else if (event.key === 'Escape') {
      close()
    } else if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'f') {
      input.current?.select()
    } else {
      return
    }
    event.preventDefault()
    // Een dialoog of paneel erboven hoort deze Escape en Enter niet ook te krijgen.
    event.stopPropagation()
  }

  return (
    <div className="ef-zoekbalk" role="search" aria-label={words.naam(fieldName)}>
      <input
        ref={input}
        type="search"
        className="ef-invoer ef-zoekbalk__vak"
        aria-label={words.naam(fieldName)}
        aria-describedby={countId}
        placeholder={words.plaatshouder}
        value={query}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          const text = event.target.value
          setQuery(text)
          search(text)
        }}
        onKeyDown={onKeyDown}
      />
      <span className="ef-zoekbalk__telling" id={countId}>
        {countText}
      </span>
      <span className="ef-sr-only" role="status">
        {announced}
      </span>
      <span className="ef-zoekbalk__knoppen">
        <BarButton label={words.vorige} disabled={total === 0} onClick={() => step(-1)}>
          <ChevronUp aria-hidden="true" size={ICON_SIZE} />
        </BarButton>
        <BarButton label={words.volgende} disabled={total === 0} onClick={() => step(1)}>
          <ChevronDown aria-hidden="true" size={ICON_SIZE} />
        </BarButton>
        <BarButton label={words.sluiten} onClick={close}>
          <X aria-hidden="true" size={ICON_SIZE} />
        </BarButton>
      </span>
    </div>
  )
}

function BarButton({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}): ReactNode {
  return (
    <button
      type="button"
      className="ef-zoekbalk__knop"
      aria-label={label}
      title={label}
      disabled={disabled}
      // De focus blijft in het zoekvak, zodat wie klikt meteen verder typt.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

const ICON_SIZE = 18

const WORDS = {
  nl: {
    naam: (field: string) => `Zoeken in ${field}`,
    plaatshouder: 'Zoek in dit veld',
    van: (at: number, total: number) => `${String(at)} van ${String(total)}`,
    geen: 'Geen treffers',
    vorige: 'Vorige treffer',
    volgende: 'Volgende treffer',
    sluiten: 'Zoeken sluiten',
  },
  en: {
    naam: (field: string) => `Find in ${field}`,
    plaatshouder: 'Find in this field',
    van: (at: number, total: number) => `${String(at)} of ${String(total)}`,
    geen: 'No matches',
    vorige: 'Previous match',
    volgende: 'Next match',
    sluiten: 'Close find',
  },
} as const
